import React, { Fragment, useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { usePrescriptionJob } from 'js/components/Prescription/PrescriptionJobContext';
import { useLangFile } from '../../../context/LanguageContext';
import DateHandler from '../../DateHandler/DateHandler';
import SelectSeasonFormControl from '../../UI-Elements/SelectSeasonFormControl';
import { setCurrentJob } from 'js/reducers/PrescriptionReducer';
import { setYieldColorIntervals, YIELD_LAYERS } from '../../../constants/YieldLayer';
import moment from 'moment-timezone';
import { formatDateAsYearMonthDate } from '../../../helpers/DateUtils';
import { useFarm } from '../../../context/AccountContext';
import {
  DATA_INTEGRATION_CLAAS_CONNECT,
  DATA_INTEGRATION_EXATREK,
} from '../../../reducers/DataIntegrationsReducer';
import { getClaasYieldValues, getExatrekYieldValues } from '../../../reducers/YieldReducer';

const getLatestImage = (yieldImages: Array) => {
  const sortedYieldInfo = yieldImages.sort(function (a, b) {
    return new Date(b.date) - new Date(a.date);
  });
  return sortedYieldInfo[0];
};

const YieldSelector = ({
  dispatch,
  setVariableColorScaleObject,
  setReferenceValues,
  setImageURL,
}) => {
  const { prescriptionJob } = usePrescriptionJob();
  const images = [...prescriptionJob.assets[YIELD_LAYERS.YIELD].images];

  const [dates, setDates] = useState();
  const [date, setDate] = useState();
  const farm = useFarm();
  const seasonId = prescriptionJob.seasonId;

  useEffect(() => {
    if (
      !prescriptionJob ||
      !prescriptionJob.assets ||
      !prescriptionJob.assets[YIELD_LAYERS.YIELD]
    ) {
      return;
    }

    setDefaultDateForSeason(seasonId);
  }, []);

  /*
    useEffect(() => {
    if (
      !seasonId ||
      !date ||
      !prescriptionJob ||
      !prescriptionJob.assets ||
      !prescriptionJob.assets[YIELD_LAYERS.YIELD]
    ) {
      return;
    }

    const image = images.find((i) => i.seasonId == seasonId && i.date == date);

    if (!image) {
      return;
    }

    setYieldColorIntervals(image.colorIntervals);
    setImageURL(image.url);
    if (image.source === DATA_INTEGRATION_CLAAS_CONNECT) {
      dispatch(getClaasYieldValues(farm.farmId, image.id)).then((result) => {
        setReferenceValues(result.values);
      });
    } else if (image.source === DATA_INTEGRATION_EXATREK) {
      dispatch(getExatrekYieldValues(image.id)).then((result) => {
        setReferenceValues(result.values);
      });
    }

    const intervals = image.colorIntervals.sort((a, b) => a.min - b.min);
    setVariableColorScaleObject(intervals);
  }, [prescriptionJob, date]);

  /* TODO: Replace this useEffect below with the outcommented useEffect above when colorIntervals for claas is also loaded on sign in */

  useEffect(() => {
    if (
      !seasonId ||
      !date ||
      !prescriptionJob ||
      !prescriptionJob.assets ||
      !prescriptionJob.assets[YIELD_LAYERS.YIELD]
    ) {
      return;
    }

    const image = images.find((i) => i.seasonId == seasonId && i.date == date);

    if (!image) {
      return;
    }

    let isCancelled = false;

    const loadYieldData = async () => {
      setImageURL(image.url);

      let valuesResult = null;
      if (image.source === DATA_INTEGRATION_CLAAS_CONNECT) {
        valuesResult = await dispatch(getClaasYieldValues(farm.farmId, image.id));
      } else if (image.source === DATA_INTEGRATION_EXATREK) {
        valuesResult = await dispatch(getExatrekYieldValues(image.id));
      }

      if (isCancelled) {
        return;
      }

      if (valuesResult?.values) {
        setReferenceValues(valuesResult.values);
      }

      const fallbackIntervals = valuesResult?.colorIntervals || valuesResult?.intervals;
      const sourceIntervals = image.colorIntervals || fallbackIntervals;

      if (sourceIntervals) {
        const sortedIntervals = [...sourceIntervals].sort((a, b) => a.min - b.min);
        setYieldColorIntervals(sortedIntervals);
        setVariableColorScaleObject(sortedIntervals);
      }
    };

    loadYieldData();

    return () => {
      isCancelled = true;
    };
  }, [prescriptionJob, date]);

  const setDefaultDateForSeason = (seasonId) => {
    const seasonImages = images.filter((i) => i.seasonId == seasonId);
    const yieldDates = seasonImages.map((i) => i.date);
    setDates(yieldDates);
    const latestSeasonImage = getLatestImage(seasonImages);
    setDate(latestSeasonImage.date);
  };

  const handleYieldDateSet = useCallback((date) => {
    const dateComponent = formatDateAsYearMonthDate(date);
    setDate(dateComponent);
  }, []);

  return <>{<DateHandler date={date} dates={dates} handleDateSet={handleYieldDateSet} />}</>;
};

YieldSelector.propTypes = {
  dispatch: PropTypes.func,
  setImageURL: PropTypes.func,
  setVariableColorScaleObject: PropTypes.func,
  setReferenceValues: PropTypes.func,
};

YieldSelector.defaultProps = {};

export default YieldSelector;
